import { useQuery } from '@tanstack/react-query';
import { fetchFunnel, fetchDrilldown, fetchBottleneck, fetchSummary, fetchDashboardConfig, fetchDiagnostic } from '@/lib/api/analytics';
import type { FunnelParams, DrilldownParams } from '@/types/analytics';

export const analyticsKeys = {
  all: ['analytics'] as const,
  funnel: (params: FunnelParams) => [...analyticsKeys.all, 'funnel', params] as const,
  drilldown: (stage: string, params: DrilldownParams) => [...analyticsKeys.all, 'drilldown', stage, params] as const,
  bottleneck: (params: FunnelParams) => [...analyticsKeys.all, 'bottleneck', params] as const,
  summary: (params: FunnelParams) => [...analyticsKeys.all, 'summary', params] as const,
  dashboardConfig: () => [...analyticsKeys.all, 'dashboardConfig'] as const,
  diagnostic: (params: FunnelParams) => [...analyticsKeys.all, 'diagnostic', params] as const,
};

export function useFunnel(params: FunnelParams = {}) {
  return useQuery({
    queryKey: analyticsKeys.funnel(params),
    queryFn: () => fetchFunnel(params),
  });
}

export function useDrilldown(stage: string | null, params: DrilldownParams = {}) {
  return useQuery({
    queryKey: analyticsKeys.drilldown(stage ?? '', params),
    queryFn: () => fetchDrilldown(stage!, params),
    enabled: !!stage,
  });
}

export function useBottleneck(params: FunnelParams = {}) {
  return useQuery({
    queryKey: analyticsKeys.bottleneck(params),
    queryFn: () => fetchBottleneck(params),
  });
}

export function useSummary(params: FunnelParams = {}) {
  return useQuery({
    queryKey: analyticsKeys.summary(params),
    queryFn: () => fetchSummary(params),
  });
}

export function useDashboardConfig() {
  return useQuery({
    queryKey: analyticsKeys.dashboardConfig(),
    queryFn: fetchDashboardConfig,
    staleTime: 5 * 60 * 1000,
  });
}

export function useDiagnostic(params: FunnelParams = {}) {
  return useQuery({
    queryKey: analyticsKeys.diagnostic(params),
    queryFn: () => fetchDiagnostic(params),
  });
}
